import { cityGuides, transportChoiceToBranch } from "@japangolmok/content";
import {
	isTripWorkspace,
	validateCompanion,
	type TripWorkspace,
} from "@japangolmok/core";
import { editions } from "@japangolmok/content";
import { getAllRecords, openDb, openStoreDb } from "./db";
import { mealChoices } from "./profile";
const stores = [
	"trips",
	"arrival-cards",
	"companion-plans",
	"profile",
	"saved-walks",
	"trash",
] as const;
export type DeviceBackup = {
	type: "japangolmok-device";
	version: 1;
	createdAt: string;
	stores: Record<string, Record<string, unknown>[]>;
};
export async function deviceBackup(photos: boolean) {
	const rows: DeviceBackup["stores"] = {};
	for (const name of stores)
		rows[name] = await getAllRecords<Record<string, unknown>>(name);
	const backup: DeviceBackup = {
		type: "japangolmok-device",
		version: 1,
		createdAt: new Date().toISOString(),
		stores: rows,
	};
	if (!photos)
		for (const name of ["trips", "trash"])
			for (const row of rows[name] ?? []) {
				const trip = (name === "trips" ? row : row.trip) as TripWorkspace;
				trip.filmMoments?.forEach((m) => {
					m.photoDataUrl = null;
					m.photoMimeType = null;
				});
			}
	return backup;
}

export function parseDeviceBackup(text: string): DeviceBackup {
	if (new Blob([text]).size > 20 * 1024 * 1024)
		throw new Error("BACKUP_TOO_LARGE");
	const b = JSON.parse(text);
	if (
		b?.type !== "japangolmok-device" ||
		b.version !== 1 ||
		!b.stores ||
		typeof b.stores !== "object"
	)
		throw new Error("INVALID_BACKUP");
	if (
		Object.keys(b.stores).some(
			(name) => !stores.includes(name as (typeof stores)[number]),
		)
	)
		throw new Error("INVALID_BACKUP");
	for (const name of stores) {
		const rows = b.stores[name];
		if (
			!Array.isArray(rows) ||
			rows.length > 1000 ||
			new Set(rows.map((r) => r?.id)).size !== rows.length
		)
			throw new Error("INVALID_BACKUP");
		for (const row of rows) {
			if (
				!row ||
				typeof row !== "object" ||
				typeof row.id !== "string" ||
				row.id.length > 128 ||
				!row.id
			)
				throw new Error("INVALID_BACKUP");
			if (name === "trips" && !isTripWorkspace(row))
				throw new Error("INVALID_BACKUP");
			if (
				name === "trash" &&
				(!isTripWorkspace(row.trip) ||
					!Number.isFinite(Date.parse(row.deletedAt)))
			)
				throw new Error("INVALID_BACKUP");
			if (
				name === "companion-plans" &&
				(row.schemaVersion !== 1 ||
					!Number.isInteger(row.revision) ||
					validateCompanion(row, false))
			)
				throw new Error("INVALID_BACKUP");
			if (name === "arrival-cards") {
				const edition = editions[row.airport as keyof typeof editions];
				const guideChoice = cityGuides[row.airport as keyof typeof cityGuides]?.transport.find((choice) => `guide-${choice.id}` === row.branch?.id);
				if (
					!edition ||
					row.schemaVersion !== 1 ||
					!row.branch ||
					(!edition.branches.some((x) => x.id === row.branch.id) && !guideChoice) ||
					!Number.isFinite(Date.parse(row.savedAt))
				)
					throw new Error("INVALID_BACKUP");
				row.branch = guideChoice ? transportChoiceToBranch(guideChoice) : edition.branches.find((x) => x.id === row.branch.id);
				row.city = edition.city;
				row.sourceUrl = guideChoice?.operatorUrl ?? edition.accessSource;
			}
			if (
				name === "profile" &&
				(row.id !== "device" ||
					!Array.isArray(row.meals) ||
					!row.meals.every((k: string) =>
						mealChoices.some((x) => x[0] === k),
					) ||
					typeof row.sound !== "boolean")
			)
				throw new Error("INVALID_BACKUP");
			if (name === "saved-walks" && typeof row.savedAt !== "string")
				throw new Error("INVALID_BACKUP");
		}
	}
	return b;
}
export async function restoreDeviceBackup(
	backup: DeviceBackup,
	mode: "both" | "replace",
) {
	parseDeviceBackup(JSON.stringify(backup));
	for (const name of stores) {
		const db = await openStoreDb(name);
		try {
			await new Promise<void>((resolve, reject) => {
				const tx = db.transaction(name, "readwrite"),
					store = tx.objectStore(name);
				for (const row of backup.stores[name] ?? []) {
					const req = store.get(row.id as string);
					req.onsuccess = () => {
						const copy = structuredClone(row);
						if (req.result && mode === "both") {
							if (name === "profile" || name === "saved-walks") return;
							copy.id = crypto.randomUUID();
							if (name === "trash")
								(copy.trip as TripWorkspace).id = copy.id as string;
						}
						store.put(copy);
					};
				}
				tx.oncomplete = () => resolve();
				tx.onerror = () => reject(tx.error);
				tx.onabort = () => reject(tx.error);
			});
		} finally {
			db.close();
		}
	}
}
export async function clearDevice() {
	for (const name of stores) {
		const db = await openStoreDb(name);
		try {
			await new Promise<void>((resolve, reject) => {
				const tx = db.transaction(name, "readwrite");
				tx.objectStore(name).clear();
				tx.oncomplete = () => resolve();
				tx.onerror = () => reject(tx.error);
				tx.onabort = () => reject(tx.error);
			});
		} finally {
			db.close();
		}
	}
	for (const key of [
		"jgm-install-hidden",
		"jgm-first-visit",
		"jgm-d7-observed",
		"jgm-correction-draft",
	]) {
		try {
			globalThis.localStorage?.removeItem(key);
		} catch {}
	}
}
export async function shrinkTripPhotos() {
	const trips = await getAllRecords<TripWorkspace>("trips");
	let before = 0,
		after = 0,
		count = 0;
	for (const trip of trips) {
		if (!isTripWorkspace(trip)) throw new Error("INVALID_TRIP");
		for (const m of trip.filmMoments ?? []) {
			if (!m.photoDataUrl) continue;
			const original = m.photoDataUrl;
			const image = new Image();
			await new Promise<void>((resolve, reject) => {
				image.onload = () => resolve();
				image.onerror = () => reject(new Error("PHOTO_READ"));
				image.src = original;
			});
			const scale = Math.min(
				1,
				1280 / Math.max(image.naturalWidth, image.naturalHeight),
			);
			const canvas = document.createElement("canvas");
			canvas.width = Math.round(image.naturalWidth * scale);
			canvas.height = Math.round(image.naturalHeight * scale);
			const ctx = canvas.getContext("2d");
			if (!ctx) throw new Error("NO_CANVAS");
			ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
			const smaller = canvas.toDataURL("image/jpeg", 0.8);
			before += original.length;
			if (smaller.length < original.length) {
				m.photoDataUrl = smaller;
				m.photoMimeType = "image/jpeg";
				after += smaller.length;
				count++;
			} else after += original.length;
		}
		if (!isTripWorkspace(trip)) throw new Error("INVALID_TRIP");
	}
	const db = await openDb();
	try {
		await new Promise<void>((resolve, reject) => {
			const tx = db.transaction("trips", "readwrite");
			trips.forEach((t) => tx.objectStore("trips").put(t));
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
			tx.onabort = () => reject(tx.error);
		});
	} finally {
		db.close();
	}
	return { before, after, count };
}
